// The icon for each kind of website component, on its own colour tile, so the
// product reads at a glance everywhere it appears (component type cards, the
// Components list, Leads): heat pump = fan, solar = sun, contact form =
// envelope, all on the brand teal for now. Colours are brand tokens (brand.ts:
// ashp/solar/contact, kept separate so they can differ again),
// so they follow the light and dark themes.
import { Fan, Mail, Sun, type LucideIcon } from "lucide-react";
import type { Widget } from "../api";
import "./productIcon.css";

export type Product = "ashp" | "solar" | "contact";

export const PRODUCT_LABELS: Record<Product, string> = { ashp: "ASHP Estimator", solar: "Solar estimate", contact: "Contact form" };
const ICONS: Record<Product, LucideIcon> = { ashp: Fan, solar: Sun, contact: Mail };

/** Which product a component is: solar by its product, contact by its style, otherwise a heat pump estimator. */
export function productOf(w: Pick<Widget, "product" | "branding">): Product {
  if (w.product === "solar") return "solar";
  return w.branding?.layout.style === "contact" ? "contact" : "ashp";
}

/**
 * The coloured tile. `size` is the tile's width and height in px (the icon is
 * scaled to fit). `labelled` adds the product name for screen readers and as a
 * tooltip; leave it off where the name is already written next to the icon.
 */
export function ProductIcon({ product, size = 28, labelled = false }: { product: Product; size?: number; labelled?: boolean }) {
  const Icon = ICONS[product];
  return (
    <span
      className={`product-icon product-icon-${product}`}
      style={{ width: size, height: size }}
      title={labelled ? PRODUCT_LABELS[product] : undefined}
      role={labelled ? "img" : undefined}
      aria-label={labelled ? PRODUCT_LABELS[product] : undefined}
      aria-hidden={labelled ? undefined : true}
    >
      <Icon size={Math.round(size * 0.56)} aria-hidden="true" />
    </span>
  );
}
